import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Skills.css';

const ViewSkill = forwardRef(({ skillId, onBack, editMode = false, createMode = false, onSkillCreated, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [skill, setSkill] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);

  const { showSuccess, showError } = useMessages();

  const skillCategories = [
    'PRINTING',
    'CUTTING',
    'LAMINATING',
    'QUALITY',
    'MAINTENANCE',
    'SETUP',
    'PACKAGING',
    'MACHINE_OPERATION',
    'MATERIAL_HANDLING',
    'OTHER'
  ];

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (skillId && !createMode) {
      fetchSkill();
      fetchSkillResources();
    } else if (createMode) {
      const newSkill = {
        skill_code: '',
        skill_name: '',
        skill_category: 'OTHER',
        description: ''
      };
      setSkill(newSkill);
      setEditData(newSkill);
    }
  }, [skillId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else if (onBack) {
          onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchSkill = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/skills/${skillId}`));
      if (response.ok) {
        const data = await response.json();
        setSkill(data);
        setEditData({
          skill_code: data.skill_code || '',
          skill_name: data.skill_name || '',
          skill_category: data.skill_category || 'OTHER',
          description: data.description || ''
        });
      } else {
        showError(t('skills.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching skill:', error);
      showError(t('skills.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSkillResources = async () => {
    try {
      const [machinesRes, usersRes] = await Promise.all([
        apiFetch(getApiUrl(`/api/skills/${skillId}/machines`)),
        apiFetch(getApiUrl(`/api/skills/${skillId}/users`))
      ]);

      if (machinesRes.ok) {
        const data = await machinesRes.json();
        setMachines(data.machines || []);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching skill resources:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!editData.skill_code?.trim()) {
      showError(tCommon('errors.validationTitle'), 'Skill code is required.');
      return;
    }
    if (!editData.skill_name?.trim()) {
      showError(tCommon('errors.validationTitle'), 'Skill name is required.');
      return;
    }

    try {
      const url = createMode
        ? getApiUrl('/api/skills/')
        : getApiUrl(`/api/skills/${skillId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const savedSkill = await response.json();
        showSuccess(createMode ? t('skills.view.messages.createdSuccess') : t('skills.view.messages.updatedSuccess'));

        if (createMode && onSkillCreated) {
          onSkillCreated(savedSkill);
        } else if (onSave) {
          onSave();
        }

        setHasChanges(false);
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving skill:', error);
      showError(tCommon('errors.genericTitle'), t('skills.view.error.loadingError'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('skills.view.confirmations.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/skills/${skillId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('skills.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting skill:', error);
      showError(tCommon('errors.genericTitle'), t('skills.view.error.fetchFailed'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm(t('skills.view.confirmations.discardChanges'))) {
        return;
      }
    }
    if (onCancel) {
      onCancel();
    }
  };

  if (loading) {
    return (
      <div className="skill-detail-view">
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
          {t('skills.loadingSkill')}
        </div>
      </div>
    );
  }

  if (!skill && !createMode) {
    return (
      <div className="skill-detail-view">
        <div className="empty-state">
          <div className="empty-state-icon">⚙️</div>
          <div className="empty-state-text">{t('skills.noData.selectSkill')}</div>
          <div className="empty-state-subtext">{t('skills.noData.createHint')}</div>
        </div>
      </div>
    );
  }

  const isEditing = editMode || createMode;

  return (
    <div className="skill-detail-view">
      {/* Header */}
      <div className="skill-detail-header">
        <div className="skill-detail-title">
          {isEditing ? (
            <input
              type="text"
              value={editData.skill_name}
              onChange={(e) => handleInputChange('skill_name', e.target.value)}
              placeholder={t('skills.view.placeholders.skillName')}
              style={{
                width: '100%',
                fontSize: '24px',
                fontWeight: '600',
                padding: '4px 8px',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
            />
          ) : (
            skill?.skill_name
          )}
        </div>
        <div className="skill-detail-subtitle">
          {isEditing ? (
            <input
              type="text"
              value={editData.skill_code}
              onChange={(e) => handleInputChange('skill_code', e.target.value.toUpperCase())}
              placeholder={t('skills.form.placeholders.skillCode')}
              style={{
                fontSize: '14px',
                fontFamily: "'Courier New', monospace",
                padding: '4px 8px',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                background: 'var(--bg-primary)',
                color: 'var(--text-secondary)'
              }}
            />
          ) : (
            skill?.skill_code
          )}
        </div>
      </div>

      {/* Basic Info */}
      <div className="skill-detail-section">
        <div className="skill-detail-section-title">{t('skills.view.sections.basicInfo')}</div>

        <div className="skill-form-group">
          <label>{t('skills.view.fields.category')}</label>
          {isEditing ? (
            <select
              value={editData.skill_category}
              onChange={(e) => handleInputChange('skill_category', e.target.value)}
            >
              {skillCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          ) : (
            <div className={`skill-category-badge skill-category-${skill?.skill_category}`}>
              {skill?.skill_category}
            </div>
          )}
        </div>

        <div className="skill-form-group">
          <label>{t('skills.view.fields.description')}</label>
          {isEditing ? (
            <textarea
              value={editData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('skills.view.placeholders.description')}
            />
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              {skill?.description || t('skills.view.fields.description')}
            </div>
          )}
        </div>
      </div>

      {/* Machines with this skill (view mode only) */}
      {!createMode && !editMode && (
        <div className="skill-detail-section">
          <div className="skill-detail-section-title">
            {t('skills.view.sections.machinesWithSkill', { count: machines.length })}
          </div>
          {machines.length > 0 ? (
            <div className="skill-resources-grid">
              {machines.map(machine => (
                <div key={machine.machine_id} className="skill-resource-card">
                  <div className="skill-resource-name">
                    {machine.machine_name}
                    <span className={`proficiency-badge proficiency-${machine.proficiency_level}`}>
                      {machine.proficiency_level}
                    </span>
                  </div>
                  <div className="skill-resource-meta">
                    {machine.machine_code} • {machine.machine_type}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '8px 0' }}>
              {t('skills.view.relatedData.noMachines')}
            </div>
          )}
        </div>
      )}

      {/* Users with this skill (view mode only) */}
      {!createMode && !editMode && (
        <div className="skill-detail-section">
          <div className="skill-detail-section-title">
            {t('skills.view.sections.usersWithSkill', { count: users.length })}
          </div>
          {users.length > 0 ? (
            <div className="skill-resources-grid">
              {users.map(user => (
                <div key={user.user_id} className="skill-resource-card">
                  <div className="skill-resource-name">
                    {user.full_name}
                    <span className={`proficiency-badge proficiency-${user.proficiency_level}`}>
                      {user.proficiency_level}
                    </span>
                  </div>
                  <div className="skill-resource-meta">
                    {user.username} • {user.role}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px', padding: '8px 0' }}>
              {t('skills.view.relatedData.noUsers')}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ViewSkill;
